import { Business, BusinessUpdate } from "@models/Business";
import { convertIdInArray, convertIdInObject } from "@util/convert-id-field";
import createFormData from "@util/create-form.data";
import { AxiosProgressEvent } from "axios";
import apiClient from "./apiClient";

interface BusinessWithId extends Omit<Business, "id"> {
    _id: string
}

export const fetchBusinesses = async (): Promise<Business[]> => {
    try {
        const response = await apiClient.get<BusinessWithId[]>("/business");
        return convertIdInArray(response.data)
    } catch (error) {
        console.error("Failed to fetch businesses", error);
        throw error;
    }
};

export const fetchBusiness = async (id: string): Promise<Business> => {
    try {
        const response = await apiClient.get<BusinessWithId>(`/business/${id}`);
        return convertIdInObject(response.data);
    } catch (error) {
        console.error("Failed to fetch businesses", error);
        throw error;
    }
};

export const createBusiness = async (
    business: BusinessUpdate,
    progressCallback?: (progressEvent: AxiosProgressEvent) => void,
): Promise<string> => {
    try {
        const formData = createFormData(business);

        const response = await apiClient.post("/business", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            onUploadProgress: progressCallback,
        });

        return response.data._id;
    } catch (error) {
        console.error("Failed to create business", error);
        throw error;
    }
};

export const updateBusiness = async (
    id: string,
    updates: BusinessUpdate,
    progressCallback?: (progressEvent: AxiosProgressEvent) => void,
): Promise<void> => {
    try {
        const formData = createFormData(updates);

        await apiClient.patch(`/business/${id}`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
            onUploadProgress: progressCallback,
        });
    } catch (error) {
        console.error("Failed to update business", error);
        throw error;
    }
};

export const setBusinessAdmin = async (id: string, adminEmail: string): Promise<void> => {
    try {
        await apiClient.patch(`/business/${id}/set-admin`, { adminEmail });
    } catch (error) {
        console.error("Failed to set business admin", error);
        throw error;
    }
};

export const acceptBusinessInvite = async (userId: string, token: string, password: string) => {
    try {
        const response = await apiClient.post("/business/accept-invite", { userId, token, password });
        return response.data;
    } catch (error) {
        console.error("Failed to accept business invitation", error);
        throw error;
    }
};
